import { formatVnd } from '@ruby-cms/shared';

// The quest form of the "Sự kiện / nhiệm vụ" page: what the operator types (strings) <-> the rule
// the API stores (campaigns.rule, cms/apps/api/src/services/quests.ts, docs/nhiem-vu.md).

export type Loai = 'cuoc' | 'nap' | 'van' | 'thang' | 'diem_danh';
export type DieuKien = 'khong' | 'cuoc' | 'nap';
export type Rule = {
  loai?: Loai; muc?: number; bac?: { muc: number; thuong: number }[]; game?: string; games?: string[];
  chuKy?: string; napCach?: 'tong' | 'lan'; napToiThieu?: number; cuocToiThieu?: number;
  ngay?: { thuong: number; dieuKien?: DieuKien; muc?: number }[]; dieuKien?: DieuKien; mucNgay?: number;
  dutChuoi?: 'lamLai' | 'noiTiep'; hetVong?: 'lapLai' | 'dung'; vongCuoc?: number;
  doiTuong?: { newDays?: number; vipMin?: number; deposited?: 'yes' | 'no'; userIds?: number[] };
  gameLobby?: string; maClient?: string;
};

export type Tier = { muc: string; thuong: string };
export type Day = { thuong: string; dieuKien: '' | DieuKien; muc: string };
export type QuestForm = {
  loai: Loai; chuKy: string; bac: Tier[]; games: string[];
  napCach: 'tong' | 'lan'; napToiThieu: string; cuocToiThieu: string;
  dieuKien: DieuKien; mucNgay: string; ngay: Day[]; dutChuoi: 'lamLai' | 'noiTiep'; hetVong: 'lapLai' | 'dung';
  vongCuoc: string; newDays: string; vipMin: string; deposited: '' | 'yes' | 'no';
  // chosen players (set through the API, not editable here): kept as they are on every save
  userIds: number[];
  gameLobby: string; maClient: string;
};

export const LOAI: [Loai, string][] = [
  ['cuoc', 'Tổng cược'], ['nap', 'Nạp tiền'], ['van', 'Số ván chơi'], ['thang', 'Số ván thắng'], ['diem_danh', 'Điểm danh hằng ngày'],
];
export const CHU_KY: [string, string][] = [['ngay', 'Mỗi ngày'], ['tuan', 'Mỗi tuần (thứ Hai đến Chủ nhật)'], ['thang', 'Mỗi tháng'], ['dot', 'Một lần trong cả đợt']];
export const DIEU_KIEN: [DieuKien, string][] = [['khong', 'Không cần gì'], ['cuoc', 'Cược đủ mức trong ngày'], ['nap', 'Nạp đủ mức trong ngày']];

export const digits = (s: string) => s.replace(/\D/g, '').slice(0, 13);
const num = (s: string) => (s.trim() === '' ? undefined : Number(s));
const str = (n: number | undefined | null) => (n == null ? '' : String(n));

export function blankForm(): QuestForm {
  return {
    loai: 'cuoc', chuKy: 'ngay', bac: [{ muc: '', thuong: '' }], games: [], napCach: 'tong', napToiThieu: '', cuocToiThieu: '',
    dieuKien: 'khong', mucNgay: '', ngay: Array.from({ length: 7 }, () => ({ thuong: '', dieuKien: '' as const, muc: '' })),
    dutChuoi: 'lamLai', hetVong: 'lapLai', vongCuoc: '', newDays: '', vipMin: '', deposited: '', userIds: [], gameLobby: '', maClient: '',
  };
}

// a stored rule back into the form; the older one-target rule becomes one tier paying `reward`
export function formOf(r: Rule | null | undefined, reward: string): QuestForm {
  const f = blankForm();
  if (!r) return f;
  const d = r.doiTuong ?? {};
  return {
    ...f,
    loai: r.loai ?? 'cuoc', chuKy: r.chuKy ?? 'dot',
    bac: r.bac?.length ? r.bac.map((t) => ({ muc: str(t.muc), thuong: str(t.thuong) })) : r.muc ? [{ muc: str(r.muc), thuong: reward }] : f.bac,
    games: r.games ?? (r.game ? [r.game] : []),
    napCach: r.napCach ?? 'tong', napToiThieu: str(r.napToiThieu), cuocToiThieu: str(r.cuocToiThieu),
    dieuKien: r.dieuKien ?? 'khong', mucNgay: str(r.mucNgay),
    ngay: r.ngay?.length ? r.ngay.map((x) => ({ thuong: str(x.thuong), dieuKien: x.dieuKien ?? '', muc: str(x.muc) })) : f.ngay,
    dutChuoi: r.dutChuoi ?? 'lamLai', hetVong: r.hetVong ?? 'lapLai', vongCuoc: str(r.vongCuoc),
    newDays: str(d.newDays), vipMin: str(d.vipMin), deposited: d.deposited ?? '', userIds: d.userIds ?? [],
    gameLobby: r.gameLobby ?? '', maClient: r.maClient ?? '',
  };
}

// The form into the rule the API validates; the first problem found, if any, in plain words.
export function ruleOf(f: QuestForm): { rule: Rule; error: string | null } {
  const rule: Rule = { loai: f.loai };
  let error: string | null = null;
  const fail = (m: string) => { error ??= m; };
  if (f.loai === 'diem_danh') {
    rule.dieuKien = f.dieuKien;
    if (num(f.mucNgay)) rule.mucNgay = num(f.mucNgay);
    rule.ngay = f.ngay.map((d, i) => {
      const x: { thuong: number; dieuKien?: DieuKien; muc?: number } = { thuong: Number(d.thuong) };
      if (!(x.thuong > 0)) fail(`Ngày ${i + 1}: nhập tiền thưởng`);
      if (d.dieuKien) x.dieuKien = d.dieuKien;
      if (num(d.muc)) x.muc = num(d.muc);
      if ((x.dieuKien ?? f.dieuKien) !== 'khong' && !(x.muc ?? rule.mucNgay)) fail(`Ngày ${i + 1}: nhập mức cược / nạp`);
      return x;
    });
    if (!rule.ngay.length) fail('Cần ít nhất một ngày');
    rule.dutChuoi = f.dutChuoi;
    rule.hetVong = f.hetVong;
  } else {
    rule.chuKy = f.chuKy;
    rule.bac = f.bac.map((t, i) => {
      const x = { muc: Number(t.muc), thuong: Number(t.thuong) };
      if (!(x.muc > 0) || !(x.thuong > 0)) fail(`Mốc ${i + 1}: nhập mức và tiền thưởng`);
      return x;
    });
    if (!rule.bac.length) fail('Cần ít nhất một mốc');
    for (let i = 1; i < rule.bac.length; i++) if (rule.bac[i]!.muc <= rule.bac[i - 1]!.muc) fail('Các mốc phải tăng dần');
    if (f.loai !== 'nap' && f.games.length) rule.games = f.games;
    if (f.loai === 'nap') {
      rule.napCach = f.napCach;
      if (num(f.napToiThieu)) rule.napToiThieu = num(f.napToiThieu);
    }
    if ((f.loai === 'van' || f.loai === 'thang') && num(f.cuocToiThieu)) rule.cuocToiThieu = num(f.cuocToiThieu);
  }
  if (f.vongCuoc.trim() !== '') {
    rule.vongCuoc = Number(f.vongCuoc);
    if (rule.vongCuoc > 100) fail('Vòng cược tối đa 100 lần');
  }
  const doiTuong: NonNullable<Rule['doiTuong']> = {};
  if (num(f.newDays)) doiTuong.newDays = num(f.newDays);
  if (num(f.vipMin)) doiTuong.vipMin = num(f.vipMin);
  if (f.deposited) doiTuong.deposited = f.deposited;
  if (f.userIds.length) doiTuong.userIds = f.userIds;
  if (Object.keys(doiTuong).length) rule.doiTuong = doiTuong;
  if (f.gameLobby.trim()) rule.gameLobby = f.gameLobby.trim();
  if (f.maClient.trim()) rule.maClient = f.maClient.trim();
  return { rule, error };
}

// What the quest's target counts, for its label and its summary
export function unitOf(loai: Loai | undefined, napCach?: string): { label: string; money: boolean } {
  if (loai === 'nap') return napCach === 'lan' ? { label: 'Số lần nạp', money: false } : { label: 'Tổng nạp', money: true };
  if (loai === 'van') return { label: 'Số ván chơi', money: false };
  if (loai === 'thang') return { label: 'Số ván thắng', money: false };
  return { label: 'Tổng cược', money: true };
}

const CHU_KY_NGAN: Record<string, string> = { ngay: '/ ngày', tuan: '/ tuần', thang: '/ tháng', dot: '/ đợt' };

// One line for the list: type, tiers (target -> reward), cycle; a check-in: days, condition, streak.
export function summaryOf(r: Rule | null | undefined, reward: string | number): string {
  if (!r) return '';
  const vnd = (n: number | string) => formatVnd(BigInt(Math.trunc(Number(n))));
  if (r.loai === 'diem_danh') {
    const n = r.ngay?.length ?? 0;
    const dk = r.dieuKien === 'cuoc' ? `cược ${vnd(r.mucNgay ?? 0)}/ngày` : r.dieuKien === 'nap' ? `nạp ${vnd(r.mucNgay ?? 0)}/ngày` : 'không điều kiện';
    // ngay co dieu kien / muc rieng: ghi ra tung ngay
    const rieng = (r.ngay ?? []).flatMap((d, i) => {
      if (d.dieuKien === undefined && d.muc === undefined) return [];
      const k = d.dieuKien ?? r.dieuKien ?? 'khong';
      const m = d.muc ?? r.mucNgay ?? 0;
      return [`ngày ${i + 1} ${k === 'cuoc' ? 'cược ' + vnd(m) : k === 'nap' ? 'nạp ' + vnd(m) : 'không điều kiện'}`];
    });
    return `Điểm danh ${n} ngày · ${dk}${rieng.length ? ' · ' + rieng.join(', ') : ''} · ${r.dutChuoi === 'noiTiep' ? 'lỡ ngày vẫn nối tiếp' : 'lỡ ngày làm lại'} · ${r.hetVong === 'dung' ? 'hết vòng thì dừng' : 'hết vòng lặp lại'}`;
  }
  const u = unitOf(r.loai, r.napCach);
  const tiers = r.bac?.length ? r.bac : r.muc ? [{ muc: r.muc, thuong: Number(reward) }] : [];
  const bac = tiers.map((t) => `${u.money ? vnd(t.muc) : t.muc} → ${vnd(t.thuong)}`).join(' | ');
  const games = r.games?.length ? r.games.join(', ') : r.game ?? '';
  return `${u.label} ${bac}${games ? ' · ' + games : ''} ${CHU_KY_NGAN[r.chuKy ?? 'dot'] ?? ''}`.trim();
}

// Warn before a check-in anyone can take with no deposit and no stake: new accounts farm it.
export function openCheckin(f: QuestForm): boolean {
  if (f.loai !== 'diem_danh' || f.deposited === 'yes' || f.vipMin) return false;
  return f.ngay.some((d) => (d.dieuKien || f.dieuKien) === 'khong');
}
